/**
 * Recovers unsaved form editor changes after a crash.
 *
 * Think of the editor as a notebook with a carbon copy underneath:
 * - While you have unsaved edits, every change is copied to a local backup (IndexedDB). Saving
 *   throws the copy away, since there is nothing left to recover.
 * - If the tab crashes or hangs, the copy stays behind. When the editor opens again and finds a
 *   copy of the same form version with edits newer than the server's, it offers a restore.
 * - If you leave on purpose (refresh, closing the tab, navigating away), the editor leaves a
 *   sticky note in localStorage saying "I left on purpose". The next load reads and removes the
 *   note, and does not offer the copy it covers.
 *
 * The note exists because throwing the copy away is async and never finishes while the page is
 * torn down, whereas writing the note is synchronous. It names the form version and the time of
 * leaving, so it never covers a copy that another tab wrote later or for a different form version.
 */
import type { FormVersion } from "../state/formVersion";
import { useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import type { Owner } from "solid-js";
import { createEffect, getOwner, on, onCleanup, runWithOwner } from "solid-js";
import { trackStore } from "@solid-primitives/deep";
import { initialized } from "../utils/initializedUtil";
import { isNil } from "lodash-es";
import type { SetStoreFunction } from "solid-js/store";
import logger from "../utils/logger";
import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "./PostHogService";
import { deserializeFormVersion, serializeFormVersion } from "../utils/serializationUtil";
import createCompressedStorage from "../utils/createCompressedStorage.ts";

const BACKUP_KEY = "lastFormVersion";
const CLEAN_EXIT_KEY = "lastFormVersionCleanExit";

type CleanExit = { formVersionId: string; exitedAt: number };

// IndexedDB instead of localStorage, which is limited to 5MiB
const storage = createCompressedStorage();

let formVersionStore: FormVersion | null = null;
let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");

let backup: FormVersion | null = null;
let owner: Owner | null = null;
let watcherEnabled = false;

const readBackup = async (): Promise<FormVersion | null> => {
  try {
    const stored = await storage.getItem(BACKUP_KEY);
    return isNil(stored) ? null : deserializeFormVersion(stored);
  } catch (e) {
    logger.error("Could not read the backed up form version", e);
    return null;
  }
};

const writeBackup = async (formVersion: FormVersion) => {
  try {
    await storage.setItem(BACKUP_KEY, serializeFormVersion(formVersion));
  } catch (e) {
    logger.error("Could not back up the form version", e);
  }
};

const clearBackup = async () => {
  try {
    await storage.removeItem(BACKUP_KEY);
  } catch (e) {
    logger.error("Could not clear the backed up form version", e);
  }
};

/** Backs up unsaved edits, and drops the backup once there is nothing left to recover, e.g. after a save. */
const syncBackup = (formVersion: FormVersion) =>
  isNil(formVersion.lastUpdatedAt) ? clearBackup() : writeBackup(formVersion);

/** Resolves with whether the user should be offered a restore. */
const init = async (): Promise<boolean> => {
  formVersionStore = useFormVersionState();
  setFormVersionStore = useFormVersionDispatch();
  owner = getOwner();
  backup = null;

  const cleanExit = consumeCleanExit();
  // Read before enabling the watcher, which clears the backup while the loaded version is unedited
  const stored = await readBackup();

  if (!shouldRestore(stored, cleanExit)) {
    enableWatcher();
    return false;
  }

  backup = stored;
  posthog.capture(POSTHOG_EVENTS.FORM_VERSION_RESTORE_REQUIRED);
  return true;
};

const enableWatcher = () => {
  if (watcherEnabled) {
    return;
  }

  watcherEnabled = true;
  // Called outside a reactive context, so the editor's owner is needed to dispose the effect with it
  runWithOwner(owner, () => {
    createEffect(
      on(
        () => trackStore(getFormVersionStore()),
        (formVersion) => void syncBackup(formVersion),
      ),
    );
    onCleanup(() => (watcherEnabled = false));
  });
};

const shouldRestore = (stored: FormVersion | null, cleanExit: CleanExit | null) => {
  try {
    if (isNil(stored) || isNil(stored.lastUpdatedAt)) {
      return false;
    }

    const formVersion = getFormVersionStore();
    const storedUpdatedAt = new Date(stored.lastUpdatedAt).getTime();
    const exitedCleanly =
      !isNil(cleanExit) && stored.id.equals(cleanExit.formVersionId) && storedUpdatedAt <= cleanExit.exitedAt;

    return (
      !exitedCleanly &&
      stored.id.equals(formVersion.id) &&
      isNil(formVersion.lastUpdatedAt) &&
      storedUpdatedAt > formVersion.meta.lastUpdated.getTime()
    );
  } catch (e) {
    logger.error("An error occurred during restore decision", e);
    return false;
  }
};

/**
 * Ends the restore decision and starts backing up. Safe to call more than once: the watcher syncs
 * the backup to the editor when it starts, so a second call cannot remove a restored backup.
 */
const reset = () => {
  backup = null;
  enableWatcher();
};

const cleanup = () => {
  backup = null;
  void clearBackup();
};

/** Applies the backup to the editor. Call {@link reset} afterwards to resume backing up. */
const restore = () => {
  if (!isNil(backup)) {
    getSetFormVersionStore()(backup);
  }
};

/** Records a deliberate page exit, so the next load does not offer to restore the backup. */
const markCleanExit = () => {
  const formVersionId = formVersionStore?.id;
  if (isNil(formVersionId)) {
    return;
  }

  try {
    const cleanExit: CleanExit = { formVersionId: formVersionId.toString(), exitedAt: Date.now() };
    localStorage.setItem(CLEAN_EXIT_KEY, JSON.stringify(cleanExit));
  } catch (e) {
    logger.error("Could not mark the form editor exit as clean", e);
  }
};

/** Undoes {@link markCleanExit} when the page returns from the back/forward cache and keeps running. */
const clearCleanExit = () => {
  try {
    localStorage.removeItem(CLEAN_EXIT_KEY);
  } catch (e) {
    logger.error("Could not clear the clean form editor exit", e);
  }
};

/** Reads and removes the clean exit, which only applies to the first load after the exit. */
const consumeCleanExit = (): CleanExit | null => {
  try {
    const stored = localStorage.getItem(CLEAN_EXIT_KEY);
    localStorage.removeItem(CLEAN_EXIT_KEY);
    return isNil(stored) ? null : (JSON.parse(stored) as CleanExit);
  } catch (e) {
    logger.error("Could not read the clean form editor exit", e);
    return null;
  }
};

const FormVersionRestoreService = {
  init,
  restore,
  reset,
  cleanup,
  markCleanExit,
  clearCleanExit,
};

export default FormVersionRestoreService;
